import React, { useEffect } from 'react';

import { useNewSizeGuideContext } from './context/useNewSizeGuideContext';

import { Text } from '../../typography/text/Text';
import { CategoriesSelector } from './CategoriesSelector';
import { Tabs } from '../../molecules/tabs/Tabs';
import { NewSizeGuideTable } from './NewSizeGuideTable';
import { Measure } from './Measure';
import { SizeCardsSlider } from './SizeCardsSlider';
import {EmailStroke, MessageStroke1, PhoneStroke } from '../../atoms/icons';

import styles from './NewSizeGuide.module.scss';
import { Drawer } from '../../molecules/drawer/Drawer';
import clsx from 'clsx';
import { ProductTableBlock } from './ProductTableBlock';
import { useMedia } from 'react-use';
import Button from '../../atoms/button';
import { theme } from './_theme';
import { useLayoutContext } from '../../../provider/layout/LayoutContext';

const toUpperCaseText = (text) => text?.toUpperCase();

const toUpperCaseTextArr = (text) => {
  if (!text) return [];
  const arr = text?.split(',');
  return arr.map((str) => toUpperCaseText(str));
};

const NewSizeGuide = ({ openChat, data }) => {
  const isMobile = useMedia('(max-width: 766px)', false);
  const { setAlwaysHideLiveChat } = useLayoutContext();
  const {
    tabs,
    activeTab,
    setActiveTab,
    activeGuide,
    isProductPage,
    state,
    setShowDialog,
    setShowChat,
    isHasMeasureTab,
  } = useNewSizeGuideContext();

  const isMeasureTab = activeTab === 'tab_1';

  useEffect(() => {
    setAlwaysHideLiveChat(!isProductPage);
    return () => setAlwaysHideLiveChat(false);
  }, []);


  const sizeConversation = activeGuide && data.sizeConversion?.find((item) =>
    toUpperCaseTextArr(item.conversionCategoryName).includes(toUpperCaseText(activeGuide?.categoryName)) &&
    (item.conversionSubcategories
        ? toUpperCaseText(item.conversionSubcategories).includes(toUpperCaseText(activeGuide?.name))
        : true
    ));

  const renderContent = (
    <div className={clsx(styles.container, { [styles.isPdpPage]: isProductPage })}>
      <div className={clsx(styles.pageTitleContainer, { [styles.hideBlockOnDesktop]: isProductPage })}>
        <Text
          align="center"
          as="p"
          className={styles.pageTitle}
        >
          Size guide
        </Text>
        {!isProductPage && (
          <CategoriesSelector />
        )}
      </div>
      <div className={clsx(styles.content, {
        [styles.isPdpPage]: isProductPage,
        [styles.noTabsTitle]: !isHasMeasureTab,
        [styles.isSabaStyle]: theme === 'saba' || theme === 'jag',
      })}>
        <Tabs
          className={styles.tabs}
          selectedIndex={!isHasMeasureTab ? 'tab_0' : activeTab}
          isCheckoutTab={false}
        >
          {tabs.map((el, idx) => {
            if (!isHasMeasureTab) {
              return null;
            }
            if (!isHasMeasureTab && idx === 1) return null;
            const { name, id } = el;
            return (
              <Tabs.Title
                onClick={() => {
                  setActiveTab(`tab_${idx}`);
                }}
                key={`name_${name}_${idx}`}
                index={id}
              >
                {name}
              </Tabs.Title>
            );
          })}
        </Tabs>
        {(isMobile && isProductPage && activeTab === tabs[0].id) && (
          <ProductTableBlock
            productData={state?.sizeGuideProductData}
            sizeConversation={activeGuide?.sizeConversation}
          />
        )}
        {activeGuide && (
          <div className={clsx(styles.sizeChart,
            {
              [styles.isPdpPage]: isProductPage,
            })}>
            {activeTab === tabs[0].id
              ? !!activeGuide && <NewSizeGuideTable isPDP={isProductPage} table={activeGuide} />
              : <Measure data={activeGuide?.measureData} isPDP={isProductPage} isMobile={isMobile} />
            }
          </div>
        )}

        {!isMeasureTab && ((isProductPage && data.showOnPdp) || (!isProductPage && data.showOnGuidePage))
          ? (
            <div className={styles.extraTextBlock}>
              <div dangerouslySetInnerHTML={{ __html: data?.extraText }} />
            </div>
          ) : null}
      </div>
      {(activeTab === tabs[0].id && sizeConversation?.conversionTable) && (
        <SizeCardsSlider isPDP={isProductPage} cards={sizeConversation.conversionTable} />
      )}
      <div className={clsx(styles.contactUsBlock, {
        [styles.isPdpPage]: isProductPage,
        [styles.isMeasureTab]: isMeasureTab,
      })}>
        {(isMeasureTab && isMobile) && (
          <span className={styles.divider} />
        )}

        {(isMeasureTab && !isMobile && isProductPage) && (
          <span className={styles.dividerDesk} />
        )}
        <h4 className={styles.contactUsTitle}>
          Still not sure about the size?
        </h4>
        <span className={styles.contactUsTitle}>
          Contact us!
        </span>

        <div className={styles.contactUsOptions}>
          {data?.showLiveChat && (
            <Button
              as="text"
              variant="primary"
              shape="square"
              size="medium"
              fill="false"
              external={true}
              isOutlined={false}
              isInverted={false}
              className={styles.optionItem}
              onClick={() => {
                setShowChat(true);
                openChat();
              }}
            >
              <MessageStroke1 />
              <span>Live chat</span>
            </Button>
          )}
          {data?.phoneNumber && (
            <Button
              as="text"
              variant="primary"
              shape="square"
              size="medium"
              href={`tel: ${data.phoneNumber}`}
              fill="false"
              external={true}
              isOutlined={false}
              isInverted={false}
              className={styles.optionItem}
            >
              <PhoneStroke />
              <span>{data.phoneNumber}</span>
            </Button>
          )}
          {data?.email && (
            <Button
              as="text"
              variant="primary"
              shape="square"
              size="medium"
              href={`mailto:${data.email}`}
              fill="false"
              external={true}
              isOutlined={false}
              isInverted={false}
              className={styles.optionItem}
            >
              <EmailStroke />
              <span>{data.email}</span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  return isProductPage
    ? state.sizeGuideOpen ? (
      <Drawer
        onClose={() => {
          setShowDialog(false);
        }}
        shown={state.sizeGuideOpen}
        width={750}
        position="right"
        customClasses={{
          closeButtonWrapper: styles.closeButtonWrapper,
          crossButton: styles.closeButton,
          asideMain: styles.drawerAside,
        }}
        type="size-guide"
        className={styles.drawerWrapper}
      >
        {!isMobile && (
          <ProductTableBlock
            productData={state?.sizeGuideProductData}
            sizeConversation={activeGuide?.sizeConversation}
          />
        )}
        {renderContent}
      </Drawer>
    ) : null
    : renderContent;
}

export default NewSizeGuide;
